import { useEffect, type RefObject } from "react";

const DRAG_THRESHOLD = 5;

/**
 * Mouse drag-to-scroll for a horizontally-scrollable container.
 * Touch is left to native momentum scrolling. A capture-phase click
 * suppressor swallows the click that follows a real drag, so dragging
 * over a button/link doesn't fire its onClick.
 *
 * pointermove can fire above 60Hz on high-refresh inputs, so the
 * scrollLeft write is rAF-batched: at most one write per animation
 * frame, regardless of how many moves arrived.
 */
export function useDragScroll(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let down = false;
    let dragged = false;
    let startX = 0;
    let startScroll = 0;
    let pendingDx: number | null = null;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      if (pendingDx === null) return;
      el.scrollLeft = startScroll - pendingDx;
      pendingDx = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      down = true;
      dragged = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!dragged && Math.abs(dx) < DRAG_THRESHOLD) return;
      dragged = true;
      pendingDx = dx;
      if (rafId === null) rafId = requestAnimationFrame(flush);
      e.preventDefault();
    };
    const onPointerUp = () => {
      down = false;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        flush();
      }
    };
    const onClickCapture = (e: MouseEvent) => {
      if (dragged) {
        e.stopPropagation();
        e.preventDefault();
        dragged = false;
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    el.addEventListener("click", onClickCapture, true);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("click", onClickCapture, true);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [ref]);
}
